import Raven from 'raven-js';
import { startServiceWorker } from './startServiceWorker';
import { unregisterServiceWorker } from './unregisterServiceWorker';
import { debug } from '../../utils/DebugUtil';
import { LEGACY_NOTIFICATIONS_SERVICE_WORKER_URL } from './constants';
import { canShowBrowserNotifications } from './canShowBrowserNotifications';
import { startPresencePing } from './presence/startPresencePing';
import { listenToPushSubscriptionChanged } from './subscription';
import { startPushNotificationSubscription } from './startPushNotificationSubscription';

/**
 * Remove the old service worker from the Navigation repository
 */
const removeUnwantedWorker = async () => {
  try {
    await unregisterServiceWorker(LEGACY_NOTIFICATIONS_SERVICE_WORKER_URL);
  } catch (error) {
    debug('Could not unregister the unwanted legacy service worker', error);
    Raven.captureBreadcrumb({
      message: 'Failed to unregister legacy service worker'
    });
    throw error;
  }
};

/**
 * This is the entry point for browser notifications support.
 * It should be called even if the browser notification gate is disabled so that we can
 * remove our existing worker if the gate was previously on
 */
export const startBrowserNotifications = async userId => {
  if ('serviceWorker' in navigator && navigator.serviceWorker) {
    try {
      startPresencePing(userId);

      // This is known to run on page load along with a lot of other initialization
      // for the app and nav components. Configuring the service worker is not a
      // critical path and we can tolerate waiting until browser is idle.
      await waitForBrowserIdle();
      await removeUnwantedWorker();
      if (!canShowBrowserNotifications()) {
        debug('browser notifications will not be enabled');
        return;
      }
      await waitForBrowserIdle();
      const registration = await startServiceWorker();
      if (!registration) {
        debug('could not start the service worker, not continuing to setup browser notifications');
        return;
      }
      await waitForBrowserIdle();
      const subscribed = await startPushNotificationSubscription(registration);
      if (subscribed) {
        listenToPushSubscriptionChanged(registration);
      }
      debug('browser notifications fully setup');
    } catch (error) {
      debug('failed to start browser notifications', error);
      Raven.captureBreadcrumb({
        message: 'Failed to register notifications service worker'
      });
      throw error;
    }
  }
};
function waitForBrowserIdle() {
  return new Promise(resolve => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => {
        resolve();
      });
    } else {
      // Safari does not support requestIdleCallback, so we use requestAnimationFrame as a fallback
      requestAnimationFrame(() => {
        resolve();
      });
    }
  });
}